import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from './api';

export type ExportFormat = 'xlsx' | 'pdf';
export type TemplateScope = 'personal' | 'shared';
export type ExportStatus = 'active' | 'probation' | 'onboarding' | 'terminated';

export const EXPORT_STATUS_OPTIONS: { value: ExportStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'probation', label: 'Probation' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'terminated', label: 'Terminated' },
];

export interface ExportFilters {
  status?: ExportStatus[];
}

export interface ExportFieldDto {
  id: string;
  label: string;
  format: string;
  permission?: string;
}

export interface ExportFieldGroup {
  key: string;
  label: string;
  fields: ExportFieldDto[];
}

export interface FieldEntry {
  fieldId: string;
  header?: string;
}

export interface ExportTemplate {
  id: string;
  name: string;
  ownerId: string;
  scope: TemplateScope;
  format: ExportFormat;
  fields: FieldEntry[];
  filters?: ExportFilters | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateInput {
  name: string;
  scope: TemplateScope;
  format: ExportFormat;
  fields: FieldEntry[];
  filters?: ExportFilters;
}

const FIELDS_KEY = ['export', 'fields'] as const;
const TEMPLATES_KEY = ['export', 'templates'] as const;

export function useExportFields(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FIELDS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ groups: ExportFieldGroup[] }>('/export/fields');
      return data.groups;
    },
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useExportTemplates(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: async () => {
      const { data } = await api.get<ExportTemplate[]>('/export/templates');
      return data;
    },
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TemplateInput) => {
      const { data } = await api.post<ExportTemplate>('/export/templates', input);
      return data;
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<TemplateInput> }) => {
      const { data } = await api.patch<ExportTemplate>(`/export/templates/${id}`, input);
      return data;
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/export/templates/${id}`);
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export interface RunExportInput {
  templateId?: string;
  fields?: FieldEntry[];
  format: ExportFormat;
  filters?: ExportFilters;
}

/** Run an export, trigger a browser download, and surface permission/format notices. */
export async function runExport(input: RunExportInput): Promise<void> {
  const res = await api.post('/export/run', input, { responseType: 'blob' });
  const blob = res.data as Blob;

  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const match = cd.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ?? `export.${input.format}`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  const omittedRaw = res.headers['x-omitted-fields'] as string | undefined;
  if (omittedRaw) {
    try {
      const omitted = JSON.parse(omittedRaw) as string[];
      if (omitted.length) {
        toast.message(
          `🔒 ${omitted.length} column${omitted.length > 1 ? 's' : ''} hidden by your permissions`,
        );
      }
    } catch {
      /* ignore malformed header */
    }
  }
  const warning = res.headers['x-export-warning'] as string | undefined;
  if (warning) toast.message(warning);
}

export function useRunExport() {
  return useMutation({
    mutationFn: runExport,
    onSuccess: () => toast.success('Export ready — check your downloads'),
    onError: () => toast.error('Export failed. Please try again.'),
  });
}
